import * as React from "react";
import { Chart } from "chart.js";
import { ChartColors, getChartDataLabelFormatter, getTitleOptions, getToolTipFormattedValue, handleLegendHover } from "../chartInitialize";
import { htmlLegendPlugin } from "../HtmlLegendPlugIn";
import { IReportChartDataSet } from "ui/Scripts/source/models/report/reportChartDataSet";
import { useEffect } from "react";

export interface IPieChartProp {
    title?: string;
    dataId: string;
    labels: string[];
    dataSets: IReportChartDataSet[];
    legendContainerId: string;
}

const getChartDataSets = (dataSets: IReportChartDataSet[]) => {
    return dataSets.map((i, index) => (
        {
            label: i.label,
            data: i.data,
            fill: "none",
            pointRadius: 2,
            borderWidth: 0,
            lineTension: 0,
            backgroundColor: ChartColors,
            borderColor: ChartColors,
            color: "#333",
            datalabels: {
                anchor: "end",
                align: "end",
                overlap: "auto",
                display: () => {
                    return i.showDataLabels;
                },
                formatter: (value, context) => getChartDataLabelFormatter(value, context, dataSets)
            }
        }
    ));
};

export const PieChart = (props: IPieChartProp) => {
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const chartRef = React.useRef<Chart | null>(null);

    const getTooltipLabel = (tooltipItem) => {
        if (tooltipItem.datasetIndex < 0 || tooltipItem.datasetIndex >= props.dataSets.length) return tooltipItem.label;
        const chartTooltipFormat = props.dataSets[tooltipItem.datasetIndex].chartValueFormatting;
        return `${tooltipItem.dataset.label || ""}: ${getToolTipFormattedValue(chartTooltipFormat, tooltipItem.raw, tooltipItem.formattedValue)}`;
    };

    const createChart = () => {
        const hasDataLabels = props.dataSets.some(ds => ds.showDataLabels);
        // @ts-expect-error
        chartRef.current = new Chart(canvasRef.current, {
            type: "pie",
            options: {
                locale: appConfig.culture,
                maintainAspectRatio: false,
                layout: {
                    padding: {
                        top: hasDataLabels ? 30 : 0,
                        bottom: hasDataLabels ? 30 : 0,
                    }
                },
                plugins: {
                    title: getTitleOptions(props.title),
                    tooltip: {
                        boxPadding: 3,
                        callbacks: {
                            label: getTooltipLabel
                        }
                    },
                    legend: {
                        display: !props.legendContainerId,
                        position: "right",
                        maxWidth: 200,
                        onHover: (evt, legendItem) => handleLegendHover(evt, legendItem, chartRef.current)
                    },
                    // @ts-ignore
                    htmlLegend: {
                        // ID of the container to put the legend in
                        containerID: props.legendContainerId,
                    },
                },
            },
            data: {
                labels: props.labels.slice(0),
                datasets: getChartDataSets(props.dataSets) as any[],
            },
            plugins: props.legendContainerId ? [htmlLegendPlugin] : []
        });
    };

    useEffect(() => {
        createChart();

        return () => {
            if (chartRef.current) {
                chartRef.current.destroy();
            }
        };
    }, []);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const hasDataLabels = props.dataSets.some(ds => ds.showDataLabels);
        chart.config.options.layout.padding = {
            top: hasDataLabels ? 30 : 0,
            bottom: hasDataLabels ? 30 : 0,
        };
        chart.config.options.plugins.title.text = props.title;
        chart.config.options.plugins.tooltip.callbacks.label = getTooltipLabel;
        chart.data.labels = props.labels.slice(0);
        chart.data.datasets = getChartDataSets(props.dataSets) as any[];
        chart.update();
    }, [props.dataId]);

    return (
        <canvas ref={canvasRef} />
    );
};